"use client";

import { useEffect } from "react";
import {
  COUNT_TARGETS,
  KEN_BURNS_TARGETS,
  LABEL_PULSE_TARGETS,
  LINES_TARGETS,
  PARALLAX_TARGETS,
  PHOTO_TARGETS,
  REVEAL_TARGETS,
  STAGGER_TARGETS,
  countUpText,
  holdFrameRadii,
  lineIndexes,
} from "@/utils/motion";

const PENDING = "data-re-reveal-pending";
const REVEALED = "data-re-revealed";
const DONE = "data-re-reveal-done";
const COUNTING = "data-re-counting";
const COUNT_VALUE = "data-re-count-value";
const PHOTO_LOADING = "data-re-photo-loading";
const PHOTO_LOADED = "data-re-photo-loaded";
const LINES_PENDING = "data-re-lines-pending";
const LINE = "data-re-line";
const KEN_BURNS_PLAY = "data-re-kenburns-play";
const LABEL_PULSE_PLAY = "data-re-label-pulse-play";
const COUNT_DURATION = 1400;
const LINES_DURATION = 700;
const LINES_STEP = 110;
// The time after the last line's rise before the headline gets its own nodes back.
const LINES_SETTLE = 100;
const STAGGER_MAX_INDEX = 5;
const ELEMENT = 1;
const TEXT = 3;
const DOCUMENT = 9;
const WORD = /([ \t\n\r\f]+)/;

const easeOut = (progress: number) => 1 - (1 - progress) ** 3;

function targetsIn(node: Node, selector: string): Element[] {
  if (node.nodeType !== ELEMENT && node.nodeType !== DOCUMENT) return [];
  const root = node as ParentNode & Partial<Pick<Element, "matches">>;
  return [
    ...(root.matches?.(selector) ? [root as Element] : []),
    ...root.querySelectorAll(selector),
  ];
}

/**
 * Wraps each word of `heading` in a line mask whose --re-line-index is the line it is on, in a copy of its nodes, so the
 * nodes React rendered stay untouched; `restore` puts them back. Spaces stay text, so the words wrap as before, and a
 * non-breaking space joins its words.
 */
export function splitLines(
  heading: Element,
  doc: Document,
): { lines: number; restore: () => void } {
  const original = [...heading.childNodes];
  const copy = heading.cloneNode(true);
  const split = (node: Node) => {
    if (node.nodeType === ELEMENT) [...node.childNodes].forEach(split);
    if (node.nodeType !== TEXT) return;
    const parts = (node.nodeValue ?? "").split(WORD).filter(Boolean);
    (node as ChildNode).replaceWith(
      ...parts.map((part) => {
        if (WORD.test(part)) return doc.createTextNode(part);
        const line = doc.createElement("span");
        const word = doc.createElement("span");
        line.setAttribute(LINE, "");
        word.append(doc.createTextNode(part));
        line.append(word);
        return line;
      }),
    );
  };
  [...copy.childNodes].forEach(split);
  heading.replaceChildren(...copy.childNodes);
  const words = [...heading.querySelectorAll<HTMLElement>(`[${LINE}]`)];
  const indexes = lineIndexes(
    words.map((word) => word.getBoundingClientRect().top),
  );
  words.forEach((word, index) =>
    word.style.setProperty("--re-line-index", String(indexes[index])),
  );
  return {
    lines: indexes.length ? Math.max(...indexes) + 1 : 0,
    restore: () => heading.replaceChildren(...original),
  };
}

/**
 * The reveal fallback for browsers without scroll-driven animations, and count-up and soft photo loading everywhere, for
 * the targets in `win.document` now and later; returns the cleanup. Only a target wholly below the fold is hidden, so
 * nothing a visitor already sees disappears, and nothing is hidden without JavaScript. With scroll-driven animations a
 * target is marked done once its reveal has played, so it plays once. A headline below the fold rises line by line as it
 * enters, unless `lines` is false, a section photo starts its Ken Burns once in view, a label that announces something
 * new pulses once half of it is in view, and a framed photo gets its own corner for its clip.
 */
export function observeMotion(
  win: Window & typeof globalThis,
  { lines = true }: { lines?: boolean } = {},
): () => void {
  if (win.matchMedia("(prefers-reduced-motion: reduce)").matches)
    return () => {};
  const { document } = win;
  const revealing = !win.CSS?.supports?.("animation-timeline: view()");
  const hidden = new Set<Element>();
  const shown = new WeakSet<Element>();
  const counted = new WeakSet<Element>();
  const counting = new Map<Element, number>();
  const loading = new Set<Element>();
  const belowFold = (element: Element) =>
    element.getBoundingClientRect().top >= win.innerHeight;
  const done = new Set<Element>();
  const settling = new Map<Element, IntersectionObserver>();
  const seen = new WeakSet<Element>();
  const depths = new Map<string, IntersectionObserver>();
  const waitingLines = new Set<Element>();
  const rising = new Map<Element, () => void>();
  const burning = new Set<Element>();
  const pulsed = new Set<Element>();

  const reveals = new win.IntersectionObserver((entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!isIntersecting) continue;
      reveals.unobserve(target);
      hidden.delete(target);
      shown.add(target);
      target.removeAttribute(PENDING);
      target.setAttribute(REVEALED, "");
    }
  });

  const settle = (target: Element) => {
    passes.unobserve(target);
    settling.get(target)?.unobserve(target);
    settling.delete(target);
    done.add(target);
    target.setAttribute(DONE, "");
  };

  // Fully in view, or out of view after it was in view: either way its reveal has ended where nobody sees the switch.
  // On scroll-driven browsers the document is the root, not the implicit one, so that in a cross-origin frame, as the
  // CMS preview is, the root margins apply and the view is the frame's own, as for view(). Only they get it: every
  // scroll-driven browser accepts a document root, while an older one (Safari < 14) throws on it.
  const passes = new win.IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting, intersectionRatio } of entries) {
        if (isIntersecting && intersectionRatio < 0.99) seen.add(target);
        else if (isIntersecting || seen.has(target)) settle(target);
      }
    },
    { ...(revealing ? {} : { root: document }), threshold: [0, 1] },
  );

  // A target taller than the view never is fully in it; its reveal has ended once its top is a whole range in.
  const depthObserver = (element: Element) => {
    const style = win.getComputedStyle(element);
    const value = (name: string) => parseFloat(style.getPropertyValue(name));
    const index = element.matches(STAGGER_TARGETS)
      ? Math.min(value("--re-reveal-index") || 0, STAGGER_MAX_INDEX)
      : 0;
    const depth = Math.ceil(
      value("--re-reveal-range") + (value("--re-stagger-range") || 0) * index,
    );
    if (!(depth > 0)) return undefined;
    const margin = `0px 0px -${depth}px 0px`;
    if (!depths.has(margin))
      depths.set(
        margin,
        new win.IntersectionObserver(
          (entries) => {
            for (const { target, isIntersecting } of entries)
              if (isIntersecting) settle(target);
          },
          { root: document, rootMargin: margin },
        ),
      );
    return depths.get(margin);
  };

  const stopCounting = (element: Element) => {
    win.cancelAnimationFrame(counting.get(element)!);
    counting.delete(element);
    element.removeAttribute(COUNTING);
    element.removeAttribute(COUNT_VALUE);
  };

  const countUp = (element: Element) => {
    const text = element.textContent ?? "";
    const start = countUpText(text, 0);
    if (start === undefined) return;
    const duration =
      parseFloat(
        win.getComputedStyle(element).getPropertyValue("--re-count-duration"),
      ) || COUNT_DURATION;
    element.setAttribute(COUNTING, "");
    element.setAttribute(COUNT_VALUE, start);
    let begun: number | undefined;
    const frame = (now: number) => {
      begun ??= now;
      const progress = Math.min(1, (now - begun) / duration);
      if (progress >= 1) return stopCounting(element);
      element.setAttribute(COUNT_VALUE, countUpText(text, easeOut(progress))!);
      counting.set(element, win.requestAnimationFrame(frame));
    };
    counting.set(element, win.requestAnimationFrame(frame));
  };

  const counts = new win.IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting || counted.has(target)) continue;
        counts.unobserve(target);
        counted.add(target);
        countUp(target);
      }
    },
    { threshold: 0.4 },
  );

  const riseLines = (heading: Element) => {
    const style = win.getComputedStyle(heading);
    const duration =
      parseFloat(style.getPropertyValue("--re-lines-duration")) ||
      LINES_DURATION;
    const step =
      parseFloat(style.getPropertyValue("--re-lines-step")) || LINES_STEP;
    const { lines, restore } = splitLines(heading, document);
    rising.set(heading, restore);
    win.setTimeout(
      () => {
        if (rising.get(heading) !== restore) return;
        rising.delete(heading);
        restore();
      },
      duration + step * Math.max(0, lines - 1) + LINES_SETTLE,
    );
  };

  const headlines = new win.IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;
        headlines.unobserve(target);
        waitingLines.delete(target);
        target.removeAttribute(LINES_PENDING);
        riseLines(target);
      }
    },
    { threshold: 0.2 },
  );

  const kenBurns = new win.IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;
        kenBurns.unobserve(target);
        burning.add(target);
        target.setAttribute(KEN_BURNS_PLAY, "");
      }
    },
    { threshold: 0.2 },
  );

  const pulses = new win.IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;
        pulses.unobserve(target);
        pulsed.add(target);
        target.setAttribute(LABEL_PULSE_PLAY, "");
      }
    },
    { threshold: 0.5 },
  );

  // Load events do not bubble, so one capturing listener on the document hears every photo.
  const photoArrived = ({ target }: Event) => {
    if (!loading.delete(target as Element)) return;
    (target as Element).removeAttribute(PHOTO_LOADING);
    (target as Element).setAttribute(PHOTO_LOADED, "");
  };

  // Leaving the attribute after the animation lets another animation on the target (a press) end without replaying it.
  const settled = (event: Event) => {
    const { animationName, target } = event as AnimationEvent;
    if (
      animationName === "re-motion-reveal" ||
      animationName === "re-motion-mask"
    )
      (target as Element).removeAttribute(REVEALED);
    if (animationName === "re-motion-photo")
      (target as Element).removeAttribute(PHOTO_LOADED);
  };

  const track = (root: Node) => {
    for (const element of targetsIn(root, REVEAL_TARGETS)) {
      if (!revealing) {
        if (done.has(element) || settling.has(element)) continue;
        passes.observe(element);
        const deep = depthObserver(element);
        if (deep) {
          settling.set(element, deep);
          deep.observe(element);
        }
        continue;
      }
      if (shown.has(element) || element.hasAttribute(REVEALED)) continue;
      if (element.hasAttribute(PENDING) || belowFold(element)) {
        element.setAttribute(PENDING, "");
        hidden.add(element);
        reveals.observe(element);
      }
    }
    for (const element of targetsIn(root, COUNT_TARGETS))
      if (!counted.has(element)) counts.observe(element);
    // A high fetch priority marks the LCP photo, which never waits.
    for (const element of targetsIn(root, PHOTO_TARGETS))
      if (
        (element as Partial<HTMLImageElement>).complete === false &&
        element.getAttribute("fetchpriority") !== "high" &&
        belowFold(element)
      ) {
        element.setAttribute(PHOTO_LOADING, "");
        loading.add(element);
      }
    for (const element of lines ? targetsIn(root, LINES_TARGETS) : [])
      if (
        !waitingLines.has(element) &&
        !rising.has(element) &&
        belowFold(element)
      ) {
        element.setAttribute(LINES_PENDING, "");
        waitingLines.add(element);
        headlines.observe(element);
      }
    for (const element of targetsIn(root, KEN_BURNS_TARGETS))
      if (!burning.has(element)) kenBurns.observe(element);
    for (const element of targetsIn(root, LABEL_PULSE_TARGETS))
      if (!pulsed.has(element)) pulses.observe(element);
    if (!revealing) holdFrameRadii(targetsIn(root, PARALLAX_TARGETS), win);
  };

  const release = (root: Node) => {
    for (const element of targetsIn(root, REVEAL_TARGETS)) {
      reveals.unobserve(element);
      hidden.delete(element);
      passes.unobserve(element);
      settling.get(element)?.unobserve(element);
      settling.delete(element);
    }
    for (const element of targetsIn(root, COUNT_TARGETS))
      counts.unobserve(element);
    for (const element of targetsIn(root, PHOTO_TARGETS))
      if (loading.delete(element)) element.removeAttribute(PHOTO_LOADING);
    for (const element of targetsIn(root, LINES_TARGETS))
      if (waitingLines.delete(element)) {
        headlines.unobserve(element);
        element.removeAttribute(LINES_PENDING);
      }
    for (const element of targetsIn(root, KEN_BURNS_TARGETS))
      kenBurns.unobserve(element);
    for (const element of targetsIn(root, LABEL_PULSE_TARGETS))
      pulses.unobserve(element);
  };

  const stopWatching = () => {
    reveals.disconnect();
    counts.disconnect();
    passes.disconnect();
    headlines.disconnect();
    kenBurns.disconnect();
    pulses.disconnect();
    for (const observer of depths.values()) observer.disconnect();
    document.removeEventListener("load", photoArrived, true);
    document.removeEventListener("error", photoArrived, true);
    document.removeEventListener("animationend", settled);
    for (const element of [...counting.keys()]) stopCounting(element);
    for (const element of hidden) element.removeAttribute(PENDING);
    for (const element of loading) element.removeAttribute(PHOTO_LOADING);
    for (const element of done) element.removeAttribute(DONE);
    for (const element of waitingLines) element.removeAttribute(LINES_PENDING);
    for (const restore of rising.values()) restore();
    rising.clear();
    for (const element of burning) element.removeAttribute(KEN_BURNS_PLAY);
    for (const element of pulsed) element.removeAttribute(LABEL_PULSE_PLAY);
  };

  document.addEventListener("load", photoArrived, true);
  document.addEventListener("error", photoArrived, true);
  document.addEventListener("animationend", settled);
  // A browser without complex selectors in :not() (Chrome < 88, Firefox < 84) throws on the targets before anything is
  // hidden; it gets no motion rather than an error that takes down the page.
  try {
    track(document);
  } catch {
    stopWatching();
    return () => {};
  }
  const changes = new win.MutationObserver((records) => {
    for (const { addedNodes, removedNodes } of records) {
      removedNodes.forEach(release);
      addedNodes.forEach(track);
    }
  });
  changes.observe(document.body, { childList: true, subtree: true });

  return () => {
    changes.disconnect();
    stopWatching();
  };
}

/**
 * Without `lines` a headline never rises line by line: while its lines rise it holds copies of the nodes React rendered,
 * and a page that renders again meanwhile, as the CMS preview does, would make React change nodes no longer in it.
 */
export default function RevealObserver({ lines = true }: { lines?: boolean }) {
  useEffect(() => observeMotion(window, { lines }), [lines]);
  return null;
}
